import * as React from 'react';
import { useContext } from 'react';
import { Cell, CellValue, ColumnInstance, Row as RTRow, TableInstance } from 'react-table';
import { Button, Col, Row, CustomInput } from 'reactstrap';
import { IUser } from '../Users/UsersTable';
import { UsersContext } from '../../contexts/UsersContext';
import { useHistory } from 'react-router-dom';
import { getJwtFromStorage } from '../../services/AuthenticationService';
import { formatDateTime } from '../../formatters/DateTimeFormatter';
import { sanitizeHTMLConfig } from '../../contexts/DomainContext';
import { IDocument } from './Documents/Documents';
import { IIncident } from '../../components/Files/FileIncidents';
const DOMPurify = require('dompurify');

export type IFileIncidentCell =
  | (TableInstance<IIncident> & {
      column: ColumnInstance<IIncident>;
      row: RTRow<IIncident>;
      cell: Cell<IIncident, string>;
      value: CellValue<string>;
    } & { children?: React.ReactNode })
  | (TableInstance<IIncident> & {
      column: ColumnInstance<IIncident>;
      row: RTRow<IIncident>;
      cell: Cell<IIncident, number>;
      value: CellValue<number>;
    } & { children?: React.ReactNode })
  | (TableInstance<IIncident> & {
      column: ColumnInstance<IIncident>;
      row: RTRow<IIncident>;
      cell: Cell<IIncident, any>;
      value: CellValue<any>;
    } & { children?: React.ReactNode });

interface IIncidentCellProps {
  cell: IFileIncidentCell;
  printIncident?: () => void;
  selectedIncidents?: string[];
  setSelectedIncidents?: (incidentIds: any) => any;
}

export const FileIncidentMetadataCell: React.FC<IIncidentCellProps> = ({ cell }) => {
  const usersContext = useContext(UsersContext);
  const users = usersContext.users;

  let userName = '';

  if (cell && cell.row.original && cell.row.original.date) {
    const userCreated = cell.row.original.userCreated;
    if (userCreated) {
      const user = users.find((l: IUser) => l.userId === userCreated);
      userName = user?.lastName.toUpperCase() + ', ' + user?.firstName;
    }
    return (
      <header className={'text-right'}>
        {userName}
        <div className={'text-muted small'}>{formatDateTime(new Date(cell.row.original.date))}</div>
      </header>
    );
  } else {
    return <></>;
  }
};

export const FileIncidentTextCell: React.FC<IIncidentCellProps> = ({ cell, printIncident }) => {
  const history = useHistory();

  const { fileId, incidentId, documents, text, userCanEdit } = cell.row.original;

  const editFileIncident = () => {
    if (fileId) {
      history.push('/files/' + fileId + '/incidents/' + incidentId);
    }
  };

  const downloadDocument = (uploadId: string) => {
    const getUrl = (uId: string) => '/api/uploads/download/' + uId + '/' + getJwtFromStorage();
    const link = document.createElement('a');
    console.log(getUrl(uploadId));
    link.href = getUrl(uploadId);
    link.setAttribute('download', 'download');
    document.body.appendChild(link);
    link.click();
  };

  if (cell && cell.row.original) {
    return (
      <Row className={'mr-0'}>
        <Col md={9} className={'mb-3 mb-md-0'}>
          <main
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(text, sanitizeHTMLConfig),
            }}
          />
          <footer className={'d-flex'} style={{ gap: 8 }}>
            {printIncident && (
              <Button size={'sm'} color={'link'} className={'pl-0'} onClick={printIncident}>
                Print
              </Button>
            )}
            <Button size={'sm'} color={'link'} className={'p-0'} onClick={() => editFileIncident()}>
              {userCanEdit ? 'Edit' : 'View'}
            </Button>
          </footer>
        </Col>
        <Col md={3}>
          {documents && documents.length > 0 && (
            <div>
              <h6>Documents</h6>
              <ul>
                {documents.map((d: IDocument) => (
                  <li key={d.documentId}>
                    <Button
                      color={'link'}
                      className={'p-0'}
                      onClick={() => downloadDocument(d.uploadId)}
                      disabled={!d.uploadId}
                      style={{ wordBreak: 'break-all', textAlign: 'left' }}
                      title={'Download "' + d.fileName + '"'}
                    >
                      {d.fileName}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Col>
      </Row>
    );
  } else {
    return <></>;
  }
};

export const FileIncidentCheckboxCell: React.FC<IIncidentCellProps> = ({
  cell,
  selectedIncidents,
  setSelectedIncidents,
}) => {
  const { incidentId } = cell.row.original;
  return (
    <article>
      <CustomInput
        key={'print-select-' + incidentId}
        type='checkbox'
        name={'isSelected'}
        id={'print-select-' + incidentId}
        label={''}
        aria-label={'Select Incident'}
        checked={selectedIncidents?.includes(incidentId)}
        onClick={() => {
          if (selectedIncidents && setSelectedIncidents) {
            setSelectedIncidents((cn: any) =>
              selectedIncidents.includes(incidentId)
                ? cn.filter((n: any) => n !== incidentId)
                : cn
                ? cn.concat([incidentId])
                : [incidentId]
            );
          }
        }}
      />
    </article>
  );
};
